// The sidebar's menu: which groups and items each user sees. Pure — no hooks,
// no rendering — so who sees what is tested without mounting the sidebar
// (sidebarSections.test.jsx). Moved out of Aside.jsx on 2026-10-01; Finance's
// billing and payment pages now sit in two dropdowns.
import React from 'react';
import HomeIcon from '@mui/icons-material/Home';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HistoryIcon from '@mui/icons-material/History';
import PaymentsIcon from '@mui/icons-material/Payments';
import LuggageIcon from '@mui/icons-material/Luggage';
import EventNoteIcon from '@mui/icons-material/EventNote';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { IntegrationInstructions } from '@mui/icons-material';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import RuleFolderOutlinedIcon from '@mui/icons-material/RuleFolderOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';

import { holdsPermission } from '@shared/utils/holdsPermission';
import { PERM } from '@shared/constants/permissions';

export const hasRole = (user, name) => (user?.roles ?? []).some((r) => r?.name === name);

/**
 * Whether an item is the page at this path — its own `match`, else exact or
 * prefix. A dropdown is active when one of its pages is.
 */
export function isItemActive(item, pathname) {
  if (item?.children) return item.children.some((child) => isItemActive(child, pathname));
  if (!item?.path) return false;
  if (item.match) return item.match(pathname);
  if (item.exact) return pathname === item.path;
  return pathname.startsWith(item.path);
}

/**
 * A dropdown of these pages — or, when the user sees only one of them, that
 * page as a plain row: a dropdown of one is a click that shows nothing new, so
 * a Travel Manager's Finance stays two plain rows.
 * @returns {object[]} items to spread into a group
 */
export function asDropdown(parent, children) {
  return children.length > 1 ? [{ ...parent, children }] : children;
}

/**
 * The groups this user sees, each with at least one item.
 * @param {{ user: object|null, unresolvedErrorCount?: number }} args
 */
export function buildSidebarSections({ user, unresolvedErrorCount = 0 }) {
  const hasRoleNamed = (name) => hasRole(user, name);
  const hasAbilityNamed = (name) => holdsPermission(user, name);
  // Sidebar groups follow a "what you do" mental model, not a role one:
  //   Workspace → your landing
  //   Travel    → booking actions (personal + staff-on-behalf-of)
  //   Operations→ MSI Staff daily transactions / history
  //   Vendor Tools → check-in flow (Vendor role)
  //   Finance   → all money: billing, payments, exceptions, fees
  //   Directory → records you maintain: customers, vendors, hotels, employees
  //   System    → true admin: users, integrations, derby switch
  // Each section is only pushed if it has at least one visible item, so
  // role-narrowing happens at item granularity (not just section).
  const sections = [];
  // MSI Staff shares the Admin permission set (product decision, mirrors
  // RolesAndPermissionsInitSeeder). Rolling MSI Staff into the `isAdmin`
  // sidebar variable is the single-touchpoint way to open every existing
  // `if (isAdmin)` block. If the two roles diverge again in the future,
  // split this — every `isAdmin || isMsiStaff` further down is intentional
  // belt-and-suspenders (harmless once MSI Staff is inside `isAdmin`).
  // `isSuperAdmin` stays a distinct variable because the Data Seeder gate
  // must remain Super-Admin only.
  const isSuperAdmin = hasRoleNamed('Super Admin');
  const isMsiStaff = hasRoleNamed('MSI Staff');
  const isAdmin = hasRoleNamed('Admin') || isSuperAdmin || isMsiStaff;
  const isTravelMgr = hasRoleNamed('Travel Manager');
  const isVendor = hasRoleNamed('Vendor');

  // 1. Workspace — everyone with a session
  sections.push({
    key: 'workspace',
    label: 'Workspace',
    items: [
      {
        key: 'home',
        label: 'Home',
        icon: <HomeIcon />,
        path: '/user-home',
        exact: true,
        disabled: !user,
        loading: !user,
      },
    ],
  });

  // 2. Travel — all reservation-related actions, both personal and staff-mode
  {
    const items = [];
    // Each item follows the permission its page checks. A Vendor holds `view
    // own reservations` without `book reservations`, and the role-name rule
    // here showed them neither (2026-10-08).
    if (hasAbilityNamed(PERM.BOOK_RESERVATIONS)) {
      items.push({
        key: 'booking',
        label: 'Book Reservation',
        icon: <LuggageIcon />,
        path: '/booking',
        match: (p) => p.startsWith('/booking'),
        disabled: !user,
        loading: !user,
      });
    }
    if (hasAbilityNamed(PERM.VIEW_OWN_RESERVATIONS)) {
      items.push({
        key: 'reservations',
        label: 'My Reservations',
        icon: <EventNoteIcon />,
        path: '/reservations',
        match: (p) => p.startsWith('/reservations'),
        disabled: !user,
        loading: !user,
      });
    }
    // Staff Reservations = "creating reservations on behalf of someone":
    // MSI Staff (call-in bookings) and Travel Managers (team).
    if (hasAbilityNamed(PERM.VIEW_EMPLOYEES)) {
      items.push({
        key: 'staff-reservations',
        label: 'Reservations',
        icon: <AssignmentIcon />,
        path: '/staff-reservations',
        match: (p) => p.startsWith('/staff-reservations'),
      });
    }
    if (items.length) sections.push({ key: 'travel', label: 'Travel', items });
  }

  // 3. Operations — Policy Reviews queue (Admin / MSI Staff / Travel Manager).
  //    Legacy "Transactions" and "Transaction History" entries (the old MSI
  //    approval flow against /lodging-transactions and /transactions/history)
  //    were removed; superseded by Policy Reviews + the new booking flow.
  if (hasAbilityNamed(PERM.VIEW_POLICY_REVIEWS)) {
    sections.push({
      key: 'operations',
      label: 'Operations',
      items: [
        {
          key: 'policy-reviews',
          label: 'Policy Reviews',
          icon: <RuleFolderOutlinedIcon />,
          path: '/policy-reviews',
          match: (p) => p.startsWith('/policy-reviews'),
        },
      ],
    });
  }

  // 4. Vendor Tools — was the check-in / folio submission workflow.
  // Removed with the legacy MsiLodgingTransaction feature when the
  // modern Encompass VCC + TreviPay flow replaced manual folio
  // submission. If the Vendor role gets new self-service pages,
  // rebuild this section around them.

  // 5. Finance — money in/out, exceptions, customer fees
  // Travel Managers see a read-only subset (their own customer's batches +
  // payment history). MSI-side roles see the full toolkit including write
  // actions and investigation tools.
  if (isAdmin || isMsiStaff || isVendor || isTravelMgr) {
    const billing = [];
    const payments = [];
    // The /billing dashboard is the MSI "generate batch" workflow (loads
    // candidates, lets staff batch them). Not useful for TMs — they only
    // see invoices MSI has already created. Labelled for what it does: its
    // parent dropdown is already called Billing.
    if (isAdmin || isMsiStaff) {
      billing.push({
        key: 'billing-main',
        label: 'Create Batch',
        icon: <ReceiptLongIcon />,
        path: '/billing',
        match: (p) => p === '/billing',
      });
    }
    billing.push({
      key: 'billing-batches',
      label: isAdmin || isMsiStaff ? 'Billing Batches' : 'Billing',
      icon: <FolderCopyIcon />,
      path: '/billing/batches',
      // Match all /billing/* routes EXCEPT the standalone ones below and
      // Hotel Charges with its stays (their own nav entry).
      match: (p) =>
        p.startsWith('/billing/') &&
        p !== '/billing' &&
        p !== '/billing/record-payment' &&
        p !== '/billing/payments' &&
        !p.startsWith('/billing/payments/') &&
        !p.startsWith('/billing/record-payment') &&
        !p.startsWith('/billing/charges') &&
        !p.startsWith('/billing/stays'),
    });
    // Every hotel charge and every stay, and where the money is — every
    // customer's, so the permission for that, as the route checks. Its "By
    // stay" view replaced Billed Reservations on 2026-09-29.
    if (hasAbilityNamed(PERM.VIEW_ALL_CUSTOMERS)) {
      billing.push({
        key: 'hotel-charges',
        label: 'Hotel Charges',
        icon: <AccountBalanceWalletOutlinedIcon />,
        path: '/billing/charges',
        match: (p) => p.startsWith('/billing/charges') || p.startsWith('/billing/stays'),
      });
    }
    // Record Payment is MSI-only (TMs can't record payments — MSI records
    // what they've received).
    if (isAdmin || isMsiStaff) {
      payments.push({
        key: 'record-payment',
        label: 'Record Payment',
        icon: <PaymentsIcon />,
        path: '/billing/record-payment',
        match: (p) => p === '/billing/record-payment' || p.startsWith('/billing/record-payment/'),
      });
    }
    // Payment History — TMs see their own customer's payment history
    // (backend scoping enforced via ScopesToManagerCustomer).
    payments.push({
      key: 'payment-history',
      label: 'Payment History',
      icon: <HistoryIcon />,
      path: '/billing/payments',
      match: (p) => p === '/billing/payments' || p.startsWith('/billing/payments/'),
    });
    // Resolving an exception writes the billing ledger (ADR-0038).
    if (hasAbilityNamed(PERM.MANAGE_BILLING)) {
      payments.push({
        key: 'payment-exceptions',
        label: 'Payment Exceptions',
        icon: <ReportProblemOutlinedIcon />,
        path: '/payment-exceptions',
        match: (p) => p.startsWith('/payment-exceptions'),
      });
    }
    const items = [
      ...asDropdown({ key: 'billing', label: 'Billing', icon: <ReceiptLongIcon /> }, billing),
      ...asDropdown({ key: 'payments', label: 'Payments', icon: <PaymentsIcon /> }, payments),
    ];
    // VCC issuance lives under Finance (previously Travel): VCCs are the
    // payment instrument for non-DerbySoft hotels, so it sits with Payment
    // Exceptions and Customer Fees, as finance staff think of it.
    if (hasAbilityNamed(PERM.MANAGE_VCC_CARDS)) {
      items.push({
        key: 'vcc-cards',
        label: 'VCCs',
        icon: <CreditCardIcon />,
        path: '/vcc-cards',
        match: (p) => p.startsWith('/vcc-cards'),
      });
    }
    if (isAdmin || isMsiStaff) {
      items.push({
        key: 'customer-fees',
        label: 'Customer Fees',
        icon: <ReceiptLongIcon />,
        path: '/customer-fees',
        match: (p) => p.startsWith('/customer-fees'),
      });
    }
    sections.push({ key: 'finance', label: 'Finance', items });
  }

  // 6. Reporting — analytics and printable/exportable reports.
  if (isAdmin || isMsiStaff || isTravelMgr) {
    sections.push({
      key: 'reporting',
      label: 'Reporting',
      items: [
        {
          key: 'reports',
          label: 'Reports',
          icon: <AssessmentOutlinedIcon />,
          path: '/reports',
          match: (p) => p.startsWith('/reports'),
        },
      ],
    });
  }

  // 7. Directory — entity records you maintain
  {
    const items = [];
    if (isAdmin || isMsiStaff) {
      items.push({
        key: 'customers',
        label: 'Customers',
        icon: <BusinessOutlinedIcon />,
        path: '/customers',
        match: (p) => p.startsWith('/customers'),
      });
    }
    // Vendor management is MSI-only. Travel Managers don't see vendors as a
    // directory — they only interact with vendors implicitly through the
    // booking flow (which uses separate `derbysoft-*` endpoints).
    if (isAdmin || isMsiStaff) {
      items.push({
        key: 'vendors',
        label: 'Vendors',
        icon: <AddBusinessIcon />,
        path: '/vendors',
        match: (p) => p === '/vendors' || p.startsWith('/vendors/'),
      });
    }
    if (isAdmin) {
      items.push({
        key: 'chains',
        label: 'Hotel Chains',
        icon: <AccountTreeIcon />,
        path: '/chains',
        match: (p) => p.startsWith('/chains'),
      });
    }
    if (isAdmin || isTravelMgr) {
      items.push({
        key: 'employees',
        label: 'Employees',
        icon: <SupervisedUserCircleIcon />,
        path: '/employees',
        match: (p) => p.startsWith('/employees'),
      });
    }
    if (items.length) sections.push({ key: 'directory', label: 'Directory', items });
  }

  // 8. System — true admin / configuration
  if (isAdmin) {
    sections.push({
      key: 'system',
      label: 'System',
      items: [
        {
          key: 'users',
          label: 'User Management',
          icon: <ManageAccountsIcon />,
          path: '/users',
          match: (p) => p.startsWith('/users'),
        },
        {
          key: 'integrations',
          label: 'Integrations',
          icon: <IntegrationInstructions />,
          path: '/integrations',
          match: (p) => p.startsWith('/integrations'),
        },
        // Error logs — same permission gate as Integrations. Badge shows
        // count of unresolved errors seen in the last 24h.
        {
          key: 'error-logs',
          label: 'Error Logs',
          icon: <BugReportOutlinedIcon />,
          path: '/admin/errors',
          match: (p) => p.startsWith('/admin/errors'),
          badge: unresolvedErrorCount,
        },
        // Audit log — who changed what (msi_activity_log); same gate.
        {
          key: 'audit-log',
          label: 'Audit Log',
          icon: <FactCheckOutlinedIcon />,
          path: '/admin/audit-log',
          match: (p) => p.startsWith('/admin/audit-log'),
        },
        {
          key: 'policies',
          label: 'Policies',
          icon: <GavelOutlinedIcon />,
          path: '/policies',
          match: (p) => p.startsWith('/policies'),
        },
        {
          key: 'tags',
          label: 'Tags',
          icon: <LabelOutlinedIcon />,
          path: '/tags',
          match: (p) => p.startsWith('/tags'),
        },
        // Seeder is Super Admin only — bootstrap/emergency tooling for
        // populating demo billing data without SSH'ing to the server.
        // Hidden from Admin and MSI Staff since they don't need it.
        ...(isSuperAdmin ? [{
          key: 'seeder',
          label: 'Data Seeder',
          icon: <ScienceOutlinedIcon />,
          path: '/admin/seeder',
          match: (p) => p.startsWith('/admin/seeder'),
        }] : []),
      ],
    });
  }

  return sections.filter((section) => section.items.length);
}
